import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismComboSelectInput,
  PrismInput,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import { not, or } from 'ember-truth-helpers';
import { CURRENCY_MAPPING } from 'frontend-utils/utils/constants/currency-mapping-constants';
import hasUnifiedSettingsActiveFeature from 'lumen/helpers/has-unified-settings-active-feature';
import type SubscriptionChannelDistribution from 'lumen/models/subscription/channel-distribution';
import type { GeneralDefaultsModel } from 'lumen/routes/hotel/settings/my-hotel/general-defaults';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { GENERAL_PROPERTY_FEATURES } from 'lumen/utils/constants/unified-settings-feature-constants';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

type SelectOption = { id: string; label: string };

const CURRENCY_OPTIONS: SelectOption[] = Object.keys(CURRENCY_MAPPING).map(
  (code) => ({ id: code, label: code }),
);

interface SettingsMyHotelFormGeneralDefaultsSignature {
  Args: {
    model: GeneralDefaultsModel;
  };
}

export default class SettingsMyHotelFormGeneralDefaults extends Component<SettingsMyHotelFormGeneralDefaultsSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: IntlService;
  @service declare toast: Services['toast'];

  @tracked hasAttemptedSave = false;

  currencyOptions = CURRENCY_OPTIONS;

  saveTask = task({ drop: true }, async (): Promise<void> => {
    this.hasAttemptedSave = true;

    if (!this.hasChanges || !this.isVatValid || !this.channelDistribution) {
      return;
    }

    try {
      const subscriptionId = this.hotelAndMarketService.hotel.id!;
      await this.channelDistribution.saveForSubscription(subscriptionId);

      this.toast.success(
        this.intl.t('settings.my_hotel.general_defaults.save_success'),
      );
      this.hasAttemptedSave = false;
    } catch (error) {
      await this.alerting.showErrorAlert();
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  get channelDistribution():
    | SubscriptionChannelDistribution
    | null
    | undefined {
    return this.args.model.requestChannelDistributionTask.last?.value;
  }

  get hasChanges(): boolean {
    return this.channelDistribution?.hasDirtyAttributes ?? false;
  }

  get isVatValid(): boolean {
    const value = this.channelDistribution?.roomPriceVatRate;

    if (value === null || value === undefined) {
      return true;
    }

    return !isNaN(value) && value >= 0 && value <= 100;
  }

  @cached
  get selectedCurrency(): SelectOption | undefined {
    return CURRENCY_OPTIONS.find(
      (c) => c.id === this.channelDistribution?.currency,
    );
  }

  get vatErrorMessage(): string | undefined {
    if (!this.isVatValid) {
      return this.intl.t('settings.my_hotel.general_defaults.vat_invalid');
    }

    return undefined;
  }

  get vatPercentage(): string {
    const rate = this.channelDistribution?.roomPriceVatRate;

    return rate === null || rate === undefined ? '' : String(rate);
  }

  @action async cancelChanges(): Promise<void> {
    const confirmed = await this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('general.unsaved_changes.title'),
      text: this.intl.t('general.unsaved_changes.description'),
      buttons: {
        cancel: {
          text: this.intl.t('general.keep_editing'),
        },
        confirm: {
          text: this.intl.t('general.discard_changes'),
        },
      },
    });

    if (!confirmed) {
      return;
    }

    const { channelDistribution } = this;

    channelDistribution?.rollbackAttributes();

    this.hasAttemptedSave = false;
  }

  @action updateCurrency(option: SelectOption): void {
    if (!this.channelDistribution) {
      return;
    }
    this.channelDistribution.currency = option.id;
  }

  @action updateVatPercentage(event: Event): void {
    if (!this.channelDistribution) {
      return;
    }
    const raw = (event.target as HTMLInputElement).value;
    this.channelDistribution.roomPriceVatRate =
      raw === '' ? null : parseFloat(raw);
  }

  <template>
    <PrismCard data-test-id="form-general-defaults">
      <:content>
        <div class="grid gap-400 pt-400">
          <div class="grid grid-cols-2 gap-400">
            {{#if
              (hasUnifiedSettingsActiveFeature
                GENERAL_PROPERTY_FEATURES.CURRENCY
              )
            }}
              <div class="grid self-start">
                <label class="prism-input__label text-200--bold">
                  {{t "settings.my_hotel.general_defaults.currency"}}
                </label>
                <PrismComboSelectInput
                  @isSearchable={{true}}
                  @isSecondary={{true}}
                  @onChange={{this.updateCurrency}}
                  @options={{this.currencyOptions}}
                  @value={{this.selectedCurrency}}
                  data-test-id="select-currency"
                />
              </div>
            {{/if}}

            {{#if
              (hasUnifiedSettingsActiveFeature
                GENERAL_PROPERTY_FEATURES.VAT_RATE
              )
            }}
              <div class="grid self-start">
                <PrismInput
                  @errorMessage={{this.vatErrorMessage}}
                  @hasErrors={{not this.isVatValid}}
                  @inlineLabel="%"
                  @isTouched={{this.hasAttemptedSave}}
                  @label={{t "settings.my_hotel.general_defaults.vat"}}
                  @onInput={{this.updateVatPercentage}}
                  @type="number"
                  @value={{this.vatPercentage}}
                  data-test-id="input-vat-percentage"
                />
              </div>
            {{/if}}
          </div>
        </div>
      </:content>

      <:footer>
        <div class="flex justify-end gap-200">
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{or (not this.hasChanges) this.saveTask.isRunning}}
            @label={{t "general.cancel"}}
            @onClick={{this.cancelChanges}}
            data-test-id="button-cancel"
          />
          <PrismButton
            @isDisabled={{not this.hasChanges}}
            @isLoading={{this.saveTask.isRunning}}
            @label={{t "general.save"}}
            @onClick={{this.saveTask.perform}}
            data-test-id="button-save"
          />
        </div>
      </:footer>
    </PrismCard>
  </template>
}
